import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Minus, Plus, Trash2, ShoppingBag, Lock, Ticket, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { useEffect, useState } from "react";
import EnrollmentProfileForm, { isEnrollmentProfileComplete } from "@/components/EnrollmentProfileForm";

export default function CartDrawer({ onRequireAuth }) {
    const { items, open, setOpen, updateQty, removeItem, subtotal, count, clear } = useCart();
    const { user } = useAuth();
    const [processing, setProcessing] = useState(false);
    const [couponInput, setCouponInput] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [totals, setTotals] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const isAuthed = user && typeof user === "object";
    const hasCourse = items.some((i) => i.kind === "course");
    const profileNeeded = hasCourse && (!profileLoaded || !isEnrollmentProfileComplete(profile));

    // Fetch profile on open (for authed users)
    useEffect(() => {
        if (open && isAuthed) {
            api.get("/me/profile")
                .then(({ data }) => { setProfile(data.profile); setProfileLoaded(true); })
                .catch(() => { setProfile(null); setProfileLoaded(true); });
        }
    }, [open, isAuthed]);

    // Recompute totals whenever cart or coupon changes
    useEffect(() => {
        if (items.length === 0) { setTotals(null); return; }
        api.post("/cart/totals", { code: appliedCoupon || "", items })
            .then(({ data }) => setTotals(data))
            .catch(() => setTotals(null));
    }, [items, appliedCoupon]);

    const applyCoupon = async () => {
        const code = (couponInput || "").trim().toUpperCase();
        if (!code) return;
        try {
            const { data } = await api.post("/coupons/validate", { code, items });
            if (data.valid) {
                setAppliedCoupon(code);
                setTotals(data.totals);
                toast.success(`Coupon ${code} applied`);
            } else {
                toast.error(data.message || "Coupon could not be applied");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput("");
    };

    const onCheckout = async () => {
        if (!isAuthed) {
            setOpen(false);
            onRequireAuth?.();
            toast.message("Please sign in to checkout");
            return;
        }
        if (items.length === 0) return;
        if (profileNeeded) {
            setProfileOpen(true);
            return;
        }
        setProcessing(true);
        try {
            const ready = await loadRazorpay();
            if (!ready) {
                toast.error("Could not load payment gateway. Try again.");
                return;
            }
            const { data } = await api.post("/payments/create-order", { items, coupon_code: appliedCoupon || undefined });
            const options = {
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: "Aham Yog Institute",
                description: `Order ${data.order_id.slice(0, 8)}`,
                order_id: data.razorpay_order_id,
                prefill: {
                    name: data.user?.name || "",
                    email: data.user?.email || "",
                },
                theme: { color: "#18addd" },
                handler: async (resp) => {
                    try {
                        await api.post("/payments/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                            order_id: data.order_id,
                        });
                        const offlineCount = items.filter((i) => i.kind === "course" && i.mode === "offline").length;
                        const onlineCount = items.filter((i) => i.kind === "course" && i.mode === "online").length;
                        const parts = [];
                        if (offlineCount) parts.push(`${offlineCount} offline studio enrollment${offlineCount === 1 ? "" : "s"}`);
                        if (onlineCount) parts.push(`${onlineCount} online enrollment${onlineCount === 1 ? "" : "s"}`);
                        const description = parts.length
                            ? `${parts.join(" + ")} confirmed (Order #${data.order_id.slice(0, 8)}).`
                            : `Order #${data.order_id.slice(0, 8)} confirmed.`;
                        toast.success("Payment successful", { description });
                        clear();
                        setOpen(false);
                    } catch (err) {
                        toast.error("Payment verification failed", {
                            description:
                                formatApiErrorDetail(err.response?.data?.detail) || err.message,
                        });
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.message("Checkout cancelled");
                    },
                },
            };
            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", (resp) => {
                toast.error("Payment failed", {
                    description: resp.error?.description || "Please try again.",
                });
            });
            rzp.open();
        } catch (err) {
            toast.error("Could not start checkout", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
                    <SheetTitle className="font-heading text-2xl">Your Cart</SheetTitle>
                    <SheetDescription>
                        {count} {count === 1 ? "item" : "items"} in your bag
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-16">
                            <div className="h-16 w-16 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center">
                                <ShoppingBag className="h-7 w-7" strokeWidth={1.5} />
                            </div>
                            <p className="mt-4 font-heading text-lg text-ink-900">
                                Your cart is empty
                            </p>
                            <p className="mt-1 text-sm text-ink-700 max-w-xs">
                                Add courses or products to begin your journey.
                            </p>
                            <Button
                                onClick={() => setOpen(false)}
                                className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                                data-testid="cart-continue-shopping"
                            >
                                Continue Browsing
                            </Button>
                        </div>
                    ) : (
                        <ul className="space-y-4" data-testid="cart-items">
                            {items.map((i) => (
                                <li
                                    key={`${i.kind}-${i.id}`}
                                    className="flex gap-3 items-start bg-white rounded-2xl p-3 border border-gray-100"
                                    data-testid={`cart-item-${i.id}`}
                                >
                                    <div className="h-20 w-20 rounded-xl overflow-hidden shrink-0 bg-paper-alt">
                                        <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-heading text-sm font-semibold text-ink-900 line-clamp-1">
                                                    {i.name}
                                                </h4>
                                                <div className="text-[11px] text-ink-700 uppercase tracking-wider mt-0.5 flex items-center gap-1.5 flex-wrap">
                                                    <span>{i.kind === "course" ? "Course" : i.meta}</span>
                                                    {i.kind === "course" && i.mode && (
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold normal-case tracking-normal ${i.mode === "offline" ? "bg-accent-50 text-accent-700" : "bg-brand-50 text-brand-700"}`}>
                                                            {i.mode === "offline" ? "Offline · Studio" : "Online"}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeItem(i.id, i.kind)}
                                                className="text-ink-400 hover:text-accent-600 transition"
                                                data-testid={`cart-remove-${i.id}`}
                                                aria-label="Remove"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                            {i.kind === "course" ? (
                                                <span className="text-xs text-ink-700">Single enrollment</span>
                                            ) : (
                                                <div className="inline-flex items-center gap-1 bg-paper-alt rounded-full p-1">
                                                    <button
                                                        onClick={() => updateQty(i.id, i.kind, i.qty - 1)}
                                                        className="h-7 w-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-brand-300 hover:text-brand-600"
                                                        data-testid={`cart-decrease-${i.id}`}
                                                        aria-label="Decrease"
                                                    >
                                                        <Minus className="h-3.5 w-3.5" />
                                                    </button>
                                                    <span className="px-2 text-sm font-semibold w-6 text-center">
                                                        {i.qty}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQty(i.id, i.kind, i.qty + 1)}
                                                        className="h-7 w-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-brand-300 hover:text-brand-600"
                                                        data-testid={`cart-increase-${i.id}`}
                                                        aria-label="Increase"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                            <div className="font-heading font-semibold text-brand-600">
                                                ₹{(i.price * i.qty).toLocaleString("en-IN")}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-gray-100 px-6 py-5 space-y-3 bg-paper-alt" data-testid="cart-summary">
                        {/* Coupon input */}
                        {!appliedCoupon ? (
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                                    <Input
                                        value={couponInput}
                                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                        placeholder="Coupon code"
                                        className="pl-9 rounded-full uppercase h-9"
                                        data-testid="cart-coupon-input"
                                    />
                                </div>
                                <Button onClick={applyCoupon} variant="outline" className="rounded-full h-9 px-4" data-testid="cart-coupon-apply">Apply</Button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between bg-brand-50 text-brand-700 rounded-full px-3 py-1.5 text-sm" data-testid="cart-coupon-applied">
                                <span className="inline-flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> <strong className="font-mono">{appliedCoupon}</strong> applied</span>
                                <button onClick={removeCoupon} aria-label="Remove coupon" className="h-5 w-5 rounded-full bg-white text-accent-700 flex items-center justify-center" data-testid="cart-coupon-remove">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        )}

                        <div className="text-sm space-y-1">
                            <div className="flex items-center justify-between text-ink-700">
                                <span>Subtotal {totals?.tax_mode === "exclusive" ? "(excl. tax)" : ""}</span>
                                <span>₹{(totals?.subtotal ?? subtotal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                            </div>
                            {totals?.gst_amount > 0 && (
                                <div className="flex items-center justify-between text-ink-700" data-testid="cart-gst-line">
                                    <span>GST {totals.tax_mode === "inclusive" ? "(incl.)" : ""}</span>
                                    <span>₹{totals.gst_amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {totals?.discount > 0 && (
                                <div className="flex items-center justify-between text-accent-700" data-testid="cart-discount-line">
                                    <span>Discount</span>
                                    <span>−₹{totals.discount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                            <span className="font-semibold text-ink-900">Total</span>
                            <span
                                className="font-heading text-2xl font-semibold text-ink-900"
                                data-testid="cart-subtotal"
                            >
                                ₹{(totals?.total ?? subtotal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <p className="text-xs text-ink-700 flex items-center gap-1.5">
                            <Lock className="h-3 w-3" /> Secured by Razorpay
                        </p>
                        {isAuthed && hasCourse && profileLoaded && !isEnrollmentProfileComplete(profile) && (
                            <div className="bg-accent-50 border border-accent-200 rounded-2xl px-3 py-2.5 flex items-start gap-2 text-xs text-ink-900" data-testid="cart-profile-required-banner">
                                <ShieldCheck className="h-4 w-4 text-accent-700 mt-0.5 shrink-0" />
                                <span><strong>Profile required.</strong> Complete your enrollment profile before paying for a course.</span>
                            </div>
                        )}
                        <Button
                            onClick={onCheckout}
                            disabled={processing}
                            className="w-full rounded-full bg-accent-500 hover:bg-accent-600 text-white shadow-accent py-5 text-base"
                            data-testid="cart-checkout-btn"
                        >
                            {processing
                                ? "Starting checkout…"
                                : !isAuthed
                                    ? "Sign in to Checkout"
                                    : (hasCourse && profileLoaded && !isEnrollmentProfileComplete(profile))
                                        ? "Complete profile to continue"
                                        : "Pay with Razorpay"}
                        </Button>
                    </div>
                )}
            </SheetContent>

            {/* Enrollment profile capture — opens when checkout requires it */}
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
                <DialogContent className="sm:max-w-4xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="cart-profile-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Complete your enrollment profile</DialogTitle>
                        <DialogDescription>
                            We need these details once before you can book a course. They'll auto-fill on your next checkout.
                        </DialogDescription>
                    </DialogHeader>
                    <EnrollmentProfileForm
                        initial={profile}
                        submitLabel="Save & continue to payment"
                        showHeader={false}
                        onSaved={(updated) => {
                            setProfile(updated);
                            setProfileOpen(false);
                            // Small delay so users see the toast, then resume checkout.
                            setTimeout(() => onCheckout(), 200);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Sheet>
    );
}
